/* ═══════════════════════════════════════════════════════════════════
   ECHO SOUNDS — bliss-aesthetic Web Audio for the memory-match ritual.
   NO video-game arcade cues. Every sound is soft, breathing, warm —
   crystal singing bowls, wind chimes, hushed choir pads. Sine-first,
   slow attacks, long tails, natural just-intonation intervals.

   Zero-storage, zero-cost, offline-first.

   Public API (unchanged so callers keep working):
     • flip()     — card open / tap  (soft bowl ping)
     • match()    — pair matched     (warm dyad bloom)
     • victory()  — board cleared    (choir pad + shimmer)
     • deal()     — deal / next set  (wind-chime cascade)
     • setMuted(bool) / isMuted() / stopAll()
   ═══════════════════════════════════════════════════════════════════ */
import { getAudioCtx } from './audioCore';
import { makeBus, bell, swish, tap, riffle, gliss } from './luxeSfx';


const MUTE_KEY = 'echo_sfx_muted';

let ctxRef = null;
let masterRef = null;

const readMuted = () => {
  try { return window.localStorage.getItem(MUTE_KEY) === '1'; }
  catch (_) { return false; }
};

export const setMuted = (m) => {
  try { window.localStorage.setItem(MUTE_KEY, m ? '1' : '0'); }
  catch (_) { /* noop */ }
};

export const isMuted = readMuted;

const getCtx = () => {
  if (readMuted()) return null;
  if (!ctxRef || !masterRef) {
    try {
      ctxRef = getAudioCtx();
      if (!ctxRef) return null;
      masterRef = ctxRef.createGain();
      masterRef.gain.value = 0.42;               // gentler overall level
      // Very subtle low-pass so highs never bite — this is the "bliss" glue.
      const master_lp = ctxRef.createBiquadFilter();
      master_lp.type = 'lowpass';
      master_lp.frequency.value = 5200;
      master_lp.Q.value = 0.6;
      masterRef.connect(master_lp);
      master_lp.connect(ctxRef.destination);
    } catch (_) { return null; }
  }
  if (ctxRef.state !== 'running') {
    try { ctxRef.resume(); } catch (_) { /* noop */ }
  }
  return ctxRef;
};

const bus = (ctx) => makeBus(ctx, masterRef);

/* ─── FLIP · velvet card turn + soft lacquer tap ─── */
export const flip = () => {
  const ctx = getCtx(); if (!ctx) return;
  const t0 = ctx.currentTime;
  swish(ctx, bus(ctx), t0, 0.14, 0.2);
  tap(ctx, bus(ctx), t0 + 0.11, 0.16);
};

/* ─── MATCH · two crystal bells in a perfect fifth ─── */
export const match = (comboMultiplier = 1) => {
  const ctx = getCtx(); if (!ctx) return;
  const t0 = ctx.currentTime;
  const lift = Math.min(1.25, 1 + (comboMultiplier - 1) * 0.06);
  bell(ctx, bus(ctx), 783.99 * lift, t0, 0.16, 1.6);
  bell(ctx, bus(ctx), 1174.66 * lift, t0 + 0.09, 0.12, 1.8);
};

/* ─── VICTORY · rising harp glissando into a bell chord ─── */
export const victory = () => {
  const ctx = getCtx(); if (!ctx) return;
  const t0 = ctx.currentTime;
  gliss(ctx, bus(ctx), t0, [261.63, 293.66, 329.63, 392, 440, 523.25, 587.33, 659.25, 783.99, 880, 1046.5], 0.075);
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => bell(ctx, bus(ctx), f, t0 + 0.9 + i * 0.03, 0.11, 2.6));
};

/* ─── DEAL · cards riffling onto the table ─── */
export const deal = () => {
  const ctx = getCtx(); if (!ctx) return;
  riffle(ctx, bus(ctx), ctx.currentTime, 10, 0.05);
};

/* Stop everything immediately (useful on modal close). */
export const stopAll = () => {
  if (masterRef) {
    try { masterRef.disconnect(); } catch (_) { /* noop */ }
  }
  masterRef = null;
};
